import { memo, useEffect } from 'react';
import { useUpdateAppointment } from '../../../context/Appointment/appointment';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocalization } from '../../../context/Localization/localization';
import { Heading } from '../../../controls/Heading/Heading';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { GoMotoComponent } from '../../../API/GoMoto.types';
import { SurveyStep2Props } from './SurveyStep2.types';

/**
 * Survey component that asks the user a Yes/No question and gives them the respective buttons
 * to answer with. When they click either Yes or No, the screen proceeds and updates the appointment
 * with their response.
 *
 * This screen skips if the dealer does not have `surveyEnabled` set to true.
 *
 * @param onSkip - {@link SurveyStep2Props.onSkip onSkip} description.
 * @param onProceed - {@link SurveyStep2Props.onProceed onProceed} description.
 */
export const SurveyStep2: GoMotoComponent<SurveyStep2Props> = memo(({
    onSkip,
    onProceed,
}) => {
    const {
        dealer: { surveyEnabled },
    } = useKioskState();
    const Localization = useLocalization();
    const { updateSurveyQuestions } = useUpdateAppointment();
    const isKiosk = isElectron();

    useEffect(() => {
        if (!surveyEnabled) {
            onSkip();
        }
    }, [onSkip, surveyEnabled]);

    if (!surveyEnabled) {
        return null;
    }

    return (
        <ScrollableBody
            heading={
                <Heading
                    roundedIcon={
                        <GMRoundedIcon
                            roundedBackgroundSize={'large'}
                            themeType={'secondary'}
                        >
                            <GMSizedIcon
                                iconType={'CSI'}
                                iconSize={'extraLarge'}
                                ariaLabel={Localization.HandshakeIcon}
                            />
                        </GMRoundedIcon>
                    }
                    primaryHeading={Localization.OneLastQuestion}
                    secondaryHeading={(isKiosk && Localization.DidTheKioskMakeItEasier) || Localization.DidTheWebsiteMakeItEasier}
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                styleType={'primary'}
                                onClick={() => {
                                    updateSurveyQuestions({ customerDidEnjoy: true });
                                    onProceed();
                                }}
                                title={Localization.ClickToConfirmCheckInEasier}
                                label={Localization.YesItDid}
                                kioskOptions={{ square: true }}
                                id={Localization.ClickToConfirmCheckInEasier}
                                ariaLabel={Localization.ClickToConfirmCheckInEasier}
                            />
                        ),
                        (
                            <CTAButton
                                styleType={'secondary'}
                                onClick={() => {
                                    updateSurveyQuestions({ customerDidEnjoy: false });
                                    onProceed();
                                }}
                                title={Localization.ClickToConfirmCheckInNotEasier}
                                label={Localization.NoItDidNot}
                                kioskOptions={{
                                    square: true,
                                    squareOptions: {
                                        roundedIcon: (
                                            <GMRoundedIcon
                                                themeType={'primary'}
                                                allowInvertedPrimary
                                                roundedBackgroundSize={'large'}
                                            >
                                                <GMSizedIcon iconSize={'custom'} iconType={'No'} />
                                            </GMRoundedIcon>
                                        ),
                                    },
                                }}
                                id={Localization.ClickToConfirmCheckInNotEasier}
                                ariaLabel={Localization.ClickToConfirmCheckInNotEasier}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
        />
    );
});
